import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import axios from 'axios';

const HeroCarousel = () => {
  const [sliderMovies, setSliderMovies] = useState([]);
  const [smartRatingOptions, setSmartRatingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, duration: 30 },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const [moviesRes, ratingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/movies/slider/items`),
        axios.get(`${BACKEND_URL}/api/smart-ratings`),
      ]);
      setSliderMovies(moviesRes.data);
      setSmartRatingOptions(ratingsRes.data);
    } catch (error) {
      console.error('Error loading slider data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSmartRatingDetails = (ratingIdOrLabel) => {
    let rating = smartRatingOptions.find(r => r._id === ratingIdOrLabel);
    if (!rating) {
      rating = smartRatingOptions.find(r => r.label === ratingIdOrLabel);
    }
    return rating;
  };

  // Truncate text to specified character limit
  const truncateText = (text, maxLength = 170) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  if (loading || sliderMovies.length === 0) {
    return (
      <div className="relative w-full h-[650px] overflow-hidden bg-black flex items-center justify-center">
        <p className="text-gray-400">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[650px] overflow-hidden">
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {sliderMovies.map((movie, index) => (
            <div key={movie._id} className="flex-[0_0_100%] min-w-0 relative">
              <img
                src={movie.header_image || movie.poster}
                alt={movie.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              
              {/* Movie Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 z-10">
              <div className="max-w-3xl">
                {/* Status Badge - Dynamically shows film status */}
                <div className={`px-4 py-2 rounded-lg inline-block mb-4 ${
                  movie.status === 'upcoming' 
                    ? 'bg-purple-600' 
                    : movie.status === 'now_playing' 
                    ? 'bg-[#FDB714]' 
                    : 'bg-gray-600'
                }`}>
                  <span className="text-black font-bold text-sm">
                    {movie.status === 'upcoming' && 'YAKINDA'}
                    {movie.status === 'now_playing' && 'VİZYONDA'}
                    {!movie.status && 'VİZYONDA'}
                  </span>
                </div>
                
                {/* Movie Title */}
                <Link 
                  to={movie.slug ? `/film/${movie.slug}` : `/movies/${movie._id}`}
                  className="block group"
                >
                  <h2 className="text-white text-4xl md:text-6xl font-bold mb-4 drop-shadow-2xl group-hover:text-[#FDB714] transition-colors duration-300 cursor-pointer">
                    {movie.title}
                  </h2>
                </Link>
                
                {/* Movie Details Row */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  {/* Duration */}
                  <div className="flex items-center gap-2 text-white/90">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-lg font-semibold">{movie.duration} dk</span>
                  </div>
                  
                  {/* Genres */}
                  {movie.genres && movie.genres.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {movie.genres.slice(0, 3).map((genre, idx) => (
                        <span 
                          key={idx}
                          className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm font-medium"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Smart Ratings - Desktop Only */}
                  {movie.smartRatings && movie.smartRatings.length > 0 && smartRatingOptions.length > 0 && (
                    <div className="hidden md:flex gap-2 ml-4">
                      {movie.smartRatings.slice(0, 3).map((ratingIdOrLabel, idx) => {
                        const ratingDetails = getSmartRatingDetails(ratingIdOrLabel);
                        if (!ratingDetails) return null;
                        return (
                          <div 
                            key={idx}
                            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-lg p-1.5 shadow-lg"
                            title={ratingDetails.label}
                          >
                            <img 
                              src={ratingDetails.icon} 
                              alt={ratingDetails.label}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Synopsis - Desktop Only */}
                <p className="hidden md:block text-white/80 text-lg leading-relaxed line-clamp-2 max-w-2xl">
                  {truncateText(movie.synopsis, 170)}
                </p>
              </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows - Desktop Only */}
      <button
        onClick={scrollPrev}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 p-3 rounded-full backdrop-blur-sm transition-all items-center justify-center"
      >
        <ChevronLeft className="text-white" size={32} />
      </button>
      <button
        onClick={scrollNext}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 p-3 rounded-full backdrop-blur-sm transition-all items-center justify-center"
      >
        <ChevronRight className="text-white" size={32} />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {sliderMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
            className={`h-2 rounded-full transition-all ${
              index === selectedIndex ? 'w-8 bg-[#FDB714]' : 'w-2 bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Swipe Indicator for Mobile */}
      <div className="md:hidden absolute bottom-16 left-1/2 -translate-x-1/2 z-20">
        <p className="text-white/60 text-xs bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full">
          ← Kaydır →
        </p>
      </div>
    </div>
  );
};

export default HeroCarousel;
